import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Mail, Phone, Shield } from 'lucide-react';
import { Button } from './ui/Button';
import toast from 'react-hot-toast';
import { ASSESSMENT_OPTIONS } from '../utils/assessmentUtils';

const AddUserDrawer = ({ isOpen, onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    phone: '',
    role: 'user',
    assessmentKey: '',
    expiresAt: ''
  });
  
  const [companyData, setCompanyData] = useState({
    companyName: '',
    customerName: ''
  });
  
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [errors, setErrors] = useState({});

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // E.164 format validation
    const e164Regex = /^\+[1-9]\d{1,14}$/;
    return e164Regex.test(phone);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number in E.164 format (e.g., +91 9876543210)';
    }

    if (!formData.role) {
      newErrors.role = 'Role must be selected';
    }

    if (String(formData.role || '').toLowerCase() === 'user' && !formData.assessmentKey) {
      newErrors.assessmentKey = 'Assessment Type is required for USER role';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    // If customer_admin, show company dialog
    if (formData.role === 'customer_admin') {
      setShowCompanyDialog(true);
      return;
    }

    // Submit for other roles
    await submitUser();
  };
  
  const submitUser = async () => {
    try {
      const payload = { ...formData };
      delete payload.assessmentKey;
      delete payload.expiresAt;
      
      // Add company details if customer_admin
      if (formData.role === 'customer_admin') {
        payload.companyName = companyData.companyName;
        payload.customerName = companyData.customerName;
      }

      if (String(formData.role || '').toLowerCase() === 'user') {
        payload.assessment_key = formData.assessmentKey;
        if (formData.expiresAt) {
          payload.expires_at = new Date(formData.expiresAt).toISOString();
        }
      }
      
      await onSubmit(payload);
      
      // Reset form on success
      setFormData({
        displayName: '',
        email: '',
        phone: '',
        role: 'user',
        assessmentKey: '',
        expiresAt: ''
      });
      setCompanyData({
        companyName: '',
        customerName: ''
      });
      setErrors({});
      setShowCompanyDialog(false);
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'role' && String(value || '').toLowerCase() !== 'user') {
        next.assessmentKey = '';
        next.expiresAt = '';
      }
      return next;
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const formatPhoneInput = (value) => {
    // Auto-format phone number
    let cleaned = value.replace(/\D/g, '');
    if (cleaned.length > 0 && !value.startsWith('+')) {
      return '+' + cleaned;
    }
    return value;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={onClose}
          />
          
          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 overflow-y-auto"
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Add New User</h2>
                  <p className="text-sm text-gray-600 mt-1">Send an invitation to a new user</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onClose}
                  className="p-2"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
                <div className="flex-1 p-6 space-y-6">
                  {/* Display Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="w-4 h-4 inline mr-2" />
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={formData.displayName}
                      onChange={(e) => handleInputChange('displayName', e.target.value)}
                      placeholder="Enter full name"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        errors.displayName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.displayName && (
                      <p className="text-red-500 text-sm mt-1">{errors.displayName}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail className="w-4 h-4 inline mr-2" />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value.toLowerCase())}
                      placeholder="user@company.com"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Phone className="w-4 h-4 inline mr-2" />
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', formatPhoneInput(e.target.value))}
                      placeholder="+91 9876543210"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        errors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Use E.164 format with country code (e.g., +91 9876543210)
                    </p>
                  </div>

                  {/* Role */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Shield className="w-4 h-4 inline mr-2" />
                      Role *
                    </label>
                    <select
                      value={formData.role}
                      onChange={(e) => handleInputChange('role', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        errors.role ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select a role</option>
                      <option value="user">User - Can take assessments</option>
                      <option value="hr">HR - Can view reports and manage users</option>
                      <option value="customer_admin">Customer Admin - Manage company users</option>
                      <option value="admin">Admin - Full system access</option>
                    </select>
                    {errors.role && (
                      <p className="text-red-500 text-sm mt-1">{errors.role}</p>
                    )}
                  </div>

                  {String(formData.role || '').toLowerCase() === 'user' ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Assessment Type *
                        </label>
                        <select
                          value={formData.assessmentKey}
                          onChange={(e) => handleInputChange('assessmentKey', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                            errors.assessmentKey ? 'border-red-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Select assessment type</option>
                          {ASSESSMENT_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {errors.assessmentKey && (
                          <p className="text-red-500 text-sm mt-1">{errors.assessmentKey}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Expires At (Optional)
                        </label>
                        <input
                          type="datetime-local"
                          value={formData.expiresAt}
                          onChange={(e) => handleInputChange('expiresAt', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        />
                      </div>
                    </>
                  ) : null}

                  {/* Info Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">What happens next?</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• User account will be created in the system</li>
                      <li>• Invitation email will be sent with temporary password</li>
                      <li>• User can log in and set up their own password</li>
                      <li>• Account will be immediately active</li>
                    </ul>
                  </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-6">
                  <div className="flex space-x-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onClose}
                      className="flex-1"
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1"
                      loading={loading}
                    >
                      Send Invitation
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
          
          {/* Company Details Dialog */}
          {showCompanyDialog && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Company Details</h3>
                <p className="text-sm text-gray-600 mb-6">Please provide company information for the customer admin</p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      value={companyData.companyName}
                      onChange={(e) => setCompanyData(prev => ({ ...prev, companyName: e.target.value }))}
                      placeholder="e.g., Acme Corp"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Customer Name *
                    </label>
                    <input
                      type="text"
                      value={companyData.customerName}
                      onChange={(e) => setCompanyData(prev => ({ ...prev, customerName: e.target.value }))}
                      placeholder="e.g., Acme Corporation"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div className="flex space-x-3 mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCompanyDialog(false)}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    onClick={submitUser}
                    className="flex-1"
                    disabled={!companyData.companyName.trim() || !companyData.customerName.trim() || loading}
                    loading={loading}
                  >
                    Create User
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
};

export default AddUserDrawer;
